import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const URL_DEBOUNCE_MS = 400;

/**
 * Keeps a single query param (default "q") in sync with URL.
 * Use for list search/filter so state survives navigation to detail and back.
 * Input is driven by local state so typing is smooth; URL is updated after a
 * short debounce so the value is restored when navigating back.
 */
export function useListSearchParam(paramName = "q"): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get(paramName) ?? "";

  const [localValue, setLocalValue] = useState(urlValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync from URL to local when URL changes (e.g. browser back/forward or initial load)
  useEffect(() => {
    setLocalValue(urlValue);
  }, [urlValue]);

  // Debounce writing to URL so we don't overwrite the input with stale URL state while typing
  const setValue = useCallback(
    (newValue: string) => {
      setLocalValue(newValue);

      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            if (newValue.trim() === "") {
              next.delete(paramName);
            } else {
              next.set(paramName, newValue);
            }
            return next;
          },
          { replace: true },
        );
      }, URL_DEBOUNCE_MS);
    },
    [paramName, setSearchParams],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return [localValue, setValue];
}
